/**
 * Apify 크리에이터 지표 API
 *
 * 얇은 Apify 프록시 서버(apify-proxy/)를 호출해 크리에이터의 최근 콘텐츠
 * 지표(평균 조회수/좋아요/댓글, 참여율, Viral Score, 활동빈도)를 가져옵니다.
 *
 * 기존 코드 컨벤션(더미/실제 분기)을 따르되, 이 모듈은 gitignore 된
 * dummyDataConfig 에 의존하지 않도록 자체 플래그를 사용합니다.
 *   - REACT_APP_APIFY_PROXY_URL : 프록시 주소 (기본 http://localhost:4000)
 *   - REACT_APP_APIFY_MOCK      : 'true' 이면 프록시 없이 프론트 더미 사용
 */
import { apifyMetricsDummyData } from '../data/apifyMetricsDummyData';
import apiClient from './axios';
// 수집 대기 시간은 creatorMetrics 와 같은 값을 쓴다(2026-10-12 점검 D2)
import { COLLECT_TIMEOUT_MS } from './creatorMetrics';

const PROXY_URL = process.env.REACT_APP_APIFY_PROXY_URL || 'http://localhost:4000';
const USE_FRONT_MOCK = String(process.env.REACT_APP_APIFY_MOCK).toLowerCase() === 'true';

/** 지원 플랫폼 (앱의 PlatformIcon 키와 일치) */
export const APIFY_PLATFORMS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'x', label: 'X (Twitter)' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'xiaohongshu', label: '샤오홍슈' },
  { key: 'lemon8', label: 'Lemon8' },
];

/**
 * 크리에이터 지표 수집
 * @param {string} platform - 플랫폼 키 (instagram, tiktok, ...)
 * @param {string[]} handles - 크리에이터 ID/URL 목록
 * @param {number} [limit] - 크리에이터당 수집 게시물 수 (기본 12)
 * @returns {Promise<{platform:string, results:Array}>}
 */
export const collectCreatorMetrics = async (platform, handles, limit) => {
  const list = (Array.isArray(handles) ? handles : [handles])
    .map((h) => String(h || '').trim())
    .filter(Boolean);

  if (USE_FRONT_MOCK) {
    console.log('[Apify API] Using front-end mock data');
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          platform,
          limit: limit || 12,
          results: list.map((handle) => apifyMetricsDummyData(platform, handle)),
        });
      }, 700);
    });
  }

  /* 서버 수집(CreatorCrawl)으로 바꿈 — 2026-10-12 태국 KOL팀 «Failed to fetch».
     이 창은 예전 Apify 프록시(기본 http://localhost:4000)를 브라우저에서 바로 불렀는데, 라이브에는 그 서버가 없어
     KOLs DB «한 명씩/여러 명» 등록이 처음부터 동작하지 않았다. 다른 KOL 분석과 같은 서버 길(/api/creator-metrics/analyze)을 쓴다.
     서버 수집은 인스타·틱톡만 된다 — 다른 플랫폼, 수집기 일시 오류는 막지 않고 «지표 없이» 등록할 수 있게 둔다. */
  return { platform, limit: limit || 12, results: await collectViaServer(platform, list) };
};

const SERVER_PLATFORMS = ['instagram', 'tiktok'];
const PROFILE_URL = {
  instagram: (h) => `https://www.instagram.com/${h}/`,
  tiktok: (h) => `https://www.tiktok.com/@${h}`,
  youtube: (h) => `https://www.youtube.com/@${h}`,
  x: (h) => `https://x.com/${h}`,
  facebook: (h) => `https://www.facebook.com/${h}`,
  xiaohongshu: (h) => `https://www.xiaohongshu.com/user/profile/${h}`,
  lemon8: (h) => `https://www.lemon8-app.com/@${h}`,
};
/** 계정이 없다고 볼 수 있는 수집기 답 — 이 경우만 «찾지 못함», 그 밖의 오류는 지표 없이 등록 가능 */
const NOT_FOUND = /CC_404|THIN_PROFILE|NOT_FOUND/i;
const cleanHandle = (h) => String(h || '').replace(/^@/, '').trim();

export async function collectViaServer(platform, handles) {
  const list = handles.map(cleanHandle).filter(Boolean);
  const base = (handle, extra) => ({
    platform, handle, profileUrl: (PROFILE_URL[platform] || ((h) => h))(handle), ...extra,
  });
  if (!SERVER_PLATFORMS.includes(platform)) {
    return list.map((handle) => base(handle, { ok: true, metrics: { followers: null }, unverified: true }));
  }
  let items = [];
  try {
    /* 2026-10-12(오픈 뒤 점검 D2): 공용 10초 타임아웃으로 불러서, 수집이 조금만 느려도 화면은 «지표 없이 등록»으로 넘어가고
       서버는 계속 수집해 크레딧만 썼다. 같은 API 를 부르는 creatorMetrics.js 와 같은 200초로 기다린다. */
    const res = await apiClient.post(
      '/api/creator-metrics/analyze',
      { items: list.map((handle) => ({ platform, handle })) },
      { timeout: COLLECT_TIMEOUT_MS },
    );
    const data = res.data?.data || res.data || {};
    items = Array.isArray(data.items) ? data.items : [];
  } catch (error) {
    console.error('[creator collect] 서버 수집 실패:', error);
  }
  return list.map((handle) => {
    const it = items.find((x) => String(x?.handle || '').toLowerCase() === handle.toLowerCase());
    if (it?.ok) {
      return base(handle, { ok: true, metrics: { ...it, followers: it.followers ?? null, bioContacts: [] } });
    }
    if (it && NOT_FOUND.test(String(it.error || ''))) {
      return base(handle, { ok: false, error: '계정을 찾을 수 없습니다 / Account not found' });
    }
    // 수집기 오류·설정 없음·응답 없음 — 지표 없이 등록할 수 있게(팔로워는 나중에 «지표 다시 받기»로)
    return base(handle, { ok: true, metrics: { followers: null }, unverified: true });
  });
}

/**
 * 팔로워 수만 가볍게 수집 (게시물 스크래핑 없음 — "팔로워 새로고침" 전용)
 * 참여율까지 다 긁는 collectCreatorMetrics보다 훨씬 빠르고, Apify 계정의 동시 실행
 * 메모리 한도에도 덜 부담을 준다.
 * @param {string} platform
 * @param {string[]} handles
 * @returns {Promise<{platform:string, results:Array}>}
 */
export const collectCreatorFollowers = async (platform, handles) => {
  const list = (Array.isArray(handles) ? handles : [handles])
    .map((h) => String(h || '').trim())
    .filter(Boolean);

  if (USE_FRONT_MOCK) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          platform,
          results: list.map((handle) => {
            const d = apifyMetricsDummyData(platform, handle);
            return { ...d, metrics: { followers: d.metrics?.followers } };
          }),
        });
      }, 300);
    });
  }

  try {
    const res = await fetch(`${PROXY_URL}/api/profile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform, handles: list }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `팔로워 수집 실패 (${res.status})`);
    }
    return await res.json();
  } catch (error) {
    console.error('[Apify API] Profile-only collect failed:', error);
    throw error;
  }
};

/** 프록시 상태 확인 */
export const getProxyHealth = async () => {
  if (USE_FRONT_MOCK) return { ok: true, mock: true };
  const res = await fetch(`${PROXY_URL}/health`);
  return res.json();
};
